import { useCallback, useEffect, useState } from 'react';
import { useVersionedForm } from '../../shared/stale-edit';
import { normalizeActiveProjectsConfig } from '../utils/dashboardConfig';
import type { DashboardConfig } from '../types';

export type DashboardConfigForm = Pick<
  DashboardConfig,
  'revenueCalculationMethod' | 'pipelineStages' | 'projectPipelineStages' | 'activeProjectsConfig'
>;

const FIELDS = [
  'revenueCalculationMethod',
  'pipelineStages',
  'projectPipelineStages',
  'activeProjectsConfig',
] as const;

const LABELS = {
  revenueCalculationMethod: 'Revenue calculation method',
  pipelineStages: 'Pipeline value stages',
  projectPipelineStages: 'Project pipeline stages',
  activeProjectsConfig: 'Active projects criteria',
};

export const configFormOf = (config: DashboardConfig): DashboardConfigForm => ({
  revenueCalculationMethod: config.revenueCalculationMethod,
  pipelineStages: config.pipelineStages ?? [],
  projectPipelineStages: config.projectPipelineStages ?? [],
  activeProjectsConfig: normalizeActiveProjectsConfig(config.activeProjectsConfig),
});

interface Params {
  open: boolean;
  onClose: () => void;
  dashboardConfig: DashboardConfig;
  onSave: (config: DashboardConfig) => Promise<void>;
  /** Re-reads the org-wide configuration after a stale refusal. */
  reloadConfig: () => Promise<DashboardConfig>;
}

/**
 * The state shared by the three tile-configuration dialogs. They edit one
 * org-wide record, so each keeps its own copy of the form while open (Cancel
 * leaves the page as it was) and merges a stale refusal in place.
 */
export function useDashboardConfigForm({ open, onClose, dashboardConfig, onSave, reloadConfig }: Params) {
  const form = useVersionedForm<DashboardConfigForm>({ fields: FIELDS, labels: LABELS });
  const { load } = form;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    load(configFormOf(dashboardConfig), dashboardConfig.version);
    setError(null);
    // Loaded when the dialog opens only: a refresh behind it must not overwrite typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const values = form.values ?? configFormOf(dashboardConfig);
  const patch = useCallback(
    (change: Partial<DashboardConfigForm>) =>
      form.setValues((prev) => ({ ...(prev ?? configFormOf(dashboardConfig)), ...change })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form.setValues, dashboardConfig],
  );

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave({ ...dashboardConfig, ...values, version: form.version ?? undefined });
      form.saved();
      onClose();
    } catch (err) {
      const merged = await form.recover(err, async () => {
        const fresh = await reloadConfig();
        return { values: configFormOf(fresh), version: fresh.version ?? 0 };
      });
      if (!merged) setError(err instanceof Error ? err.message : 'Could not save the configuration');
    } finally {
      setSaving(false);
    }
  };

  return { form, values, patch, submit, saving, error };
}
